import type { Reducer } from 'redux';
import { Map } from 'immutable';
import { add, startOfMonth, sub } from 'date-fns';

import type { LinearChartItem } from 'features/dashboard/ts/LinearCharItem';
import { ChartViewMode } from 'features/dashboard/ts/ChartViewMode';
import { LinearGraphAction, DashboardActions } from 'features/dashboard/redux/actions';
import {
  SET_LINEAR_GRAPH_MODE,
  SET_LINEAR_GRAPH_DATA,
  SET_LINEAR_GRAPH_LOADING,
  SHIFT_LINEAR_GRAPH,
} from 'features/dashboard/redux/types';
import { LOGOUT_SUCCESS } from 'features/auth/redux/types';
import { chartModeToDuration } from 'features/dashboard/utils/chartModeToDuration';

export type LinearGraphReducerType = 'ordersGraph' | 'redemptionsGraph';

export interface LinearGraphReducerState {
  monthlyGraph: Map<number, LinearChartItem[]>;
  weeklyGraph: Map<number, LinearChartItem[]>;
  dailyGraph: Map<number, LinearChartItem[]>;
  isLoading: boolean;
  mode: ChartViewMode;
  rangeAnchor: Date;
}

const INITIAL_STATE: LinearGraphReducerState = {
  monthlyGraph: Map<number, LinearChartItem[]>(),
  weeklyGraph: Map<number, LinearChartItem[]>(),
  dailyGraph: Map<number, LinearChartItem[]>(),
  mode: ChartViewMode.Monthly,
  rangeAnchor: startOfMonth(new Date()),
  isLoading: false,
};

export const createLinearChartReducer = (reducerType: LinearGraphReducerType, apiUrl: string) => {
  const reducer: Reducer<LinearGraphReducerState, DashboardActions> = (
    state = INITIAL_STATE,
    action
  ): LinearGraphReducerState => {
    if ((action as LinearGraphAction).reducerType && (action as LinearGraphAction).reducerType !== reducerType) {
      return state;
    }

    (action as LinearGraphAction).apiUrl = apiUrl;

    switch (action.type) {
      case LOGOUT_SUCCESS:
        return INITIAL_STATE;
      case SET_LINEAR_GRAPH_MODE:
        return {
          ...state,
          mode: action.payload,
        };
      case SET_LINEAR_GRAPH_LOADING:
        return {
          ...state,
          isLoading: action.payload,
        };
      case SET_LINEAR_GRAPH_DATA:
        const newState = { ...state };

        const cacheKey = state.rangeAnchor.getTime();

        switch (state.mode) {
          case ChartViewMode.Daily:
            newState.dailyGraph = newState.dailyGraph.set(cacheKey, action.payload);
            break;
          case ChartViewMode.Weekly:
            newState.weeklyGraph = newState.weeklyGraph.set(cacheKey, action.payload);
            break;
          case ChartViewMode.Monthly:
            newState.monthlyGraph = newState.monthlyGraph.set(cacheKey, action.payload);
            break;
        }

        return newState;
      case SHIFT_LINEAR_GRAPH:
        const shiftDuration = chartModeToDuration(state.mode);

        const shift = action.payload === 'next' ? add : sub;

        return {
          ...state,
          rangeAnchor: shift(state.rangeAnchor, shiftDuration),
        };

      default:
        return state;
    }
  };

  return reducer;
};
